package one.healthium.onboarding.ui.screens

import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import loco.ONBOARDING_WELCOME_NO_ACCOUNT_BUTTON
import loco.ONBOARDING_WELCOME_SIGN_IN_BUTTON
import one.healthium.bundle.components.BundleImage
import one.healthium.domain.common.Color
import one.healthium.onboarding.domain.configs.WelcomeScreenConfig
import one.healthium.onboarding.ui.UserDataProvider
import one.healthium.onboarding.ui.UserProfileUiModel
import one.healthium.onboarding.ui.bindPlaceholderValues
import one.healthium.ui.compose.components.AppButton
import one.healthium.ui.compose.components.AppButtonDefaults
import one.healthium.ui.compose.components.AppOutlinedButton
import one.healthium.ui.compose.components.AppText
import one.healthium.ui.compose.theme.AppTheme
import org.jetbrains.compose.resources.stringResource

interface WelcomeEventDelegate {
    fun onSignInClicked()
    fun onNextClicked()
}

@Composable
fun WelcomeScreen(eventDelegate: WelcomeEventDelegate, screenConfig: WelcomeScreenConfig) {
    UserDataProvider { userState, _ ->
        WelcomeScreenContent(
            screenConfig = screenConfig,
            userState = userState,
            onSignInClicked = eventDelegate::onSignInClicked,
            onNextClicked = eventDelegate::onNextClicked,
        )
    }
}

@Composable
private fun WelcomeScreenContent(
    modifier: Modifier = Modifier,
    screenConfig: WelcomeScreenConfig,
    userState: UserProfileUiModel,
    onSignInClicked: () -> Unit,
    onNextClicked: () -> Unit,
) {
    Column(
        modifier = modifier
            .fillMaxSize()
            .gradientBackground(
                backgroundGradient = screenConfig.backgroundGradient,
                fallbackContainerColor = AppTheme.colors.windowBackground,
            )
            .statusBarsPadding()
            .navigationBarsPadding(),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Spacer(Modifier.weight(1f))

        BundleImage(
            modifier = Modifier.size(64.dp),
            name = screenConfig.appIcon,
        )
        Spacer(Modifier.height(16.dp))
        AppText(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 24.dp),
            text = screenConfig.title?.bindPlaceholderValues(userState),
            style = AppTheme.textStyles.secondary_24_600,
            color = AppTheme.colors.textInverted100,
            textAlign = TextAlign.Center,
        )
        Spacer(Modifier.height(12.dp))
        AppText(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 24.dp),
            text = screenConfig.subtitle?.bindPlaceholderValues(userState),
            style = AppTheme.textStyles.secondary_18_400,
            color = AppTheme.colors.textInverted100,
            textAlign = TextAlign.Start,
        )
        Spacer(Modifier.height(48.dp))
        AppButton(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 38.dp)
                .testTag("sign_in_button"),
            onClick = onSignInClicked,
            colors = AppButtonDefaults.invertedPrimaryButtonColors
        ) {
            Text(stringResource(loco.Res.string.ONBOARDING_WELCOME_SIGN_IN_BUTTON))
        }
        Spacer(Modifier.height(24.dp))
        AppOutlinedButton(
            modifier = Modifier.widthIn(min = 183.dp).heightIn(min = 43.dp),
            onClick = onNextClicked,
        ) {
            Text(stringResource(loco.Res.string.ONBOARDING_WELCOME_NO_ACCOUNT_BUTTON))
        }
        Spacer(Modifier.height(24.dp))
    }
}

@Composable
@Preview(showBackground = true)
private fun WelcomeScreenContentPreview() {
    AppTheme {
        WelcomeScreenContent(
            modifier = Modifier,
            screenConfig = WelcomeScreenConfig(
                appIcon = "carnivore_notification_icon_ios",
                title = "Welcome to\nVivid: Carnivore Diet App🔥",
                subtitle = "This is the beginning of a new, powerful step toward your health and weight loss goals. 🚀\n\nRemember, you’re not alone—we’re here to support you every step of the way! ❤️",
                backgroundGradient = listOf(
                    Color(0xFFFA5779, 0xFFFA5779),
                    Color(0xFFFFBB38, 0xFFFFBB38),
                ),
            ),
            userState = UserProfileUiModel.preview,
            onNextClicked = {},
            onSignInClicked = {},
        )
    }
}